import { Dispatch, SetStateAction, useState, FC, useEffect } from "react";
import {
  AutocompleteChangeReason,
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Grid2,
  IconButton,
  SelectChangeEvent,
  Stack,
  Typography
} from "@mui/material";
import { Add, Close, Remove } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { OptionSelect } from "./OptionSelect";
import { OptionCreate } from "./OptionCreate";
import { OptionDialogButtonGroup } from "./OptionDialogButtonGroup";
import { IEquipmentInventory } from "../../../../../Domain/Types/FloorPlan/Equipment.type";
import {
  addEquipmentOptions,
  clearEquipmentOptions,
  removeEquipmentOptions
} from "./Partial/WorkplaceEquipmentSelectActions";

type Props<T, Y> = {
  subjectEntityName: "Inventory" | "Zone" | "Category" | "Equipment";
  pivotEntityName: "Zone" | "Place";
  displayChipValue: any;
  displayHeaderId: number;
  onClose: Dispatch<SetStateAction<void>>;
  subjectEntity: Y | Y[];
  subjectEntityEntries: any[];
  newSubjectEntityTemplate?: T;
  CreateForm?: FC<{
    newEntity: T | undefined;
    setNewEntity: Dispatch<SetStateAction<T | undefined>>;
  }>;
  createFunction?: (subjectEntity: T | undefined) => void;
  updateFunctionSub1?: (pivotEntity: Y | Y[], selectedSubjectEntity: T) => void;
  updateFunctionSub2?: (pivotEntity: Y, selectedSubjectEntity: T[]) => void;
};

export const OptionDialogContent = <T, Y>({
  subjectEntityName,
  pivotEntityName,
  displayChipValue,
  displayHeaderId,
  onClose,
  subjectEntity,
  subjectEntityEntries,
  newSubjectEntityTemplate,
  CreateForm,
  createFunction,
  updateFunctionSub1,
  updateFunctionSub2
}: Props<T, Y>) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [newSubjectEntity, setNewSubjectEntity] = useState<T | undefined>(newSubjectEntityTemplate);
  const [selectedSubjectEntity, setSelectedSubjectEntity] = useState<T | T[] | undefined>(
    undefined
  );
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const [availableEntities, setAvailableEntities] = useState<any[]>([]);
  const [currentEntities, setCurrentEntities] = useState<any[]>([]);

  const componentPreparedToCreate = CreateForm && createFunction && newSubjectEntityTemplate;

  // category is nullable
  const nullEntry = {
    id: 0,
    description: "Unassigned",
    name: t("Unassigned"),
    costFactor: 0
  };

  function handleChange(event: SelectChangeEvent) {
    const findSubjectEntity = [...subjectEntityEntries, nullEntry].filter(
      item => item.id === (event.target.value as unknown as number)
    );

    if (findSubjectEntity.length === 1) {
      setSelectedSubjectEntity(findSubjectEntity[0]);
    } else {
      setSelectedSubjectEntity(findSubjectEntity);
    }
  }

  function handleEquipmentChange(value: any[], reasons: AutocompleteChangeReason) {
    switch (reasons) {
      case "selectOption":
        addEquipmentOptions({
          value,
          availableEquipment: availableEntities,
          currentEquipment: currentEntities,
          setAvailableEquipment: setAvailableEntities,
          setCurrentEquipment: setCurrentEntities,
          enqueueSnackbar,
          t
        });
        setSelectedSubjectEntity(value);
        break;
      case "removeOption":
        removeEquipmentOptions({
          value,
          availableEquipment: availableEntities,
          currentEquipment: currentEntities,
          setAvailableEquipment: setAvailableEntities,
          setCurrentEquipment: setCurrentEntities,
          enqueueSnackbar,
          t
        });
        setSelectedSubjectEntity(value);
        break;
      case "clear":
        clearEquipmentOptions({
          availableEquipment: availableEntities,
          currentEquipment: currentEntities,
          setAvailableEquipment: setAvailableEntities,
          setCurrentEquipment: setCurrentEntities
        });
        setSelectedSubjectEntity([]);
        break;
      default:
        return;
    }
  }

  const translationKeys = {
    Category: {
      singular: "category",
      plural: "categories"
    },
    Inventory: {
      singular: "inventory",
      plural: "inventories"
    },
    Zone: {
      singular: "zone",
      plural: "zones"
    },
    Equipment: {
      singular: "equipment",
      plural: "equipment"
    },
    Place: {
      singular: "place",
      plural: "places"
    }
  };

  const title = (() => {
    if (Array.isArray(subjectEntity) && subjectEntity.length > 1) {
      return t("OptionDialogContentEditMultipleTitle", {
        subjectEntity: t(translationKeys[subjectEntityName].singular)
      });
    }

    if (pivotEntityName == "Zone" && subjectEntityName === "Category") {
      return t("AssignZoneCategoryTitle");
    }
    if (pivotEntityName == "Zone" && subjectEntityName === "Equipment") {
      return t("AssignZoneEquimentTitle");
    }

    if (pivotEntityName == "Place" && subjectEntityName === "Category") {
      return t("AssignPlaceCategoryTitle");
    }
    if (pivotEntityName == "Place" && subjectEntityName === "Equipment") {
      return t("AssignPlaceEquimentTitle");
    }

    return t("OptionDialogContentEditTitle", {
      subjectEntity: t(translationKeys[subjectEntityName].singular),
      pivotEntity: t(translationKeys[pivotEntityName].singular)
    });
  })();

  useEffect(() => {
    const equipmentConfig = () => {
      const avail = subjectEntityEntries[0].availableEquipment as IEquipmentInventory[];
      const assgined = subjectEntityEntries[0].assignedEquipment;

      setAvailableEntities(avail);
      setCurrentEntities(assgined);
    };

    if (subjectEntityEntries && subjectEntityName === "Equipment") equipmentConfig();
  }, [subjectEntityEntries, displayHeaderId]);

  return (
    <Card sx={{ p: 2, maxHeight: "80vh", width: "20vw" }} data-testid={"option-assignment"}>
      <Stack direction={"column"} gap={2}>
        <Grid2 container>
          <Grid2 size={"grow"} />
          <Grid2 size={{ xs: 9 }} sx={{ marginBottom: "10px" }}>
            <Typography align={"center"} lineHeight={2.5}>
              {title}
            </Typography>
          </Grid2>
          <Grid2 size={"grow"} sx={{ textAlign: "right" }}>
            <IconButton
              sx={{ float: "right" }}
              data-testid="option-btn-group-close"
              onClick={() => {
                setSelectedSubjectEntity(undefined);
                onClose();
              }}
              size="large"
            >
              <Close />
            </IconButton>
          </Grid2>
        </Grid2>
        {subjectEntity && subjectEntityName !== "Equipment" && (
          <Grid2 data-testid="option-dialog-content-name" display={"flex"} alignItems={"center"}>
            <Typography>{t(subjectEntityName)}</Typography>
            {":  "}
            <Chip label={displayChipValue} />
          </Grid2>
        )}

        <Box gap={1} display={"grid"} alignItems={"center"}>
          <OptionSelect
            entries={[nullEntry, ...subjectEntityEntries]}
            subjectEntityName={subjectEntityName}
            defaultValue={""}
            onChange={handleChange}
            onEquipmentChange={handleEquipmentChange}
            availableEntries={availableEntities}
            currentEntries={currentEntities}
          />
          {componentPreparedToCreate && (
            <>
              <Typography display={"flex"} justifyContent={"center"}>
                {t("or")}
              </Typography>
              <Button
                size={"small"}
                sx={{ flex: 1 }}
                startIcon={isCreating ? <Remove /> : <Add />}
                onClick={() => {
                  setIsCreating(value => !value);
                }}
                data-testid="option-btn-group-create"
              >
                {t("OptionDialogContentCreateNewLabel", { name: subjectEntityName })}
              </Button>
            </>
          )}
        </Box>

        {componentPreparedToCreate && (
          <Collapse in={isCreating}>
            <OptionCreate subjectEntityName={subjectEntityName}>
              <CreateForm newEntity={newSubjectEntity} setNewEntity={setNewSubjectEntity} />
            </OptionCreate>
          </Collapse>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", pl: 0 }}>
          <OptionDialogButtonGroup
            isCreating={isCreating}
            setIsCreating={setIsCreating}
            onClose={onClose}
            handleChange={() => {
              if (Array.isArray(selectedSubjectEntity)) {
                updateFunctionSub2?.(subjectEntity as Y, selectedSubjectEntity);
                return;
              }

              if (selectedSubjectEntity !== undefined) {
                updateFunctionSub1?.(subjectEntity, selectedSubjectEntity);
              }
            }}
            handleCreate={() => {
              setNewSubjectEntity(newSubjectEntityTemplate);

              if (CreateForm && createFunction && newSubjectEntityTemplate)
                createFunction(newSubjectEntity);
            }}
          />
        </Box>
      </Stack>
    </Card>
  );
};
